import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import SendIcon from "@material-ui/icons/Send";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getContactDisplayName } from "../../helpers/contactDisplayName";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflow: "auto",
    ...theme.scrollbarStyles
  },
  followUps: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  modalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(2)
  },
  messageCell: {
    maxWidth: 360,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  actionCell: {
    whiteSpace: "nowrap"
  }
}));

const initialDraft = {
  name: "",
  message: "",
  whatsappId: "",
  segment: "",
  followUpHours: 48
};

const statusLabel = {
  draft: "Borrador",
  active: "En curso",
  paused: "Pausada",
  completed: "Finalizada"
};

const statusColor = status => {
  if (status === "active") return "primary";
  if (status === "paused") return "secondary";
  return "default";
};

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();
  const [campaigns, setCampaigns] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [segments, setSegments] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState(initialDraft);
  const [saving, setSaving] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [campaignResponse, followUpResponse, segmentResponse, whatsappResponse] =
        await Promise.all([
          api.get("/campaigns"),
          api.get("/campaigns/follow-ups"),
          api.get("/campaigns/segments"),
          api.get("/whatsapp/")
        ]);
      setCampaigns(campaignResponse.data.campaigns || []);
      setFollowUps(followUpResponse.data.followUps || []);
      setSegments(segmentResponse.data.segments || []);
      setWhatsapps(whatsappResponse.data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const connectedWhatsapps = whatsapps.filter(
    whatsapp => whatsapp.status === "CONNECTED"
  );

  const handleDraftChange = event => {
    const { name, value } = event.target;
    setDraft(previous => ({ ...previous, [name]: value }));
  };

  const handleCreate = async () => {
    if (!draft.name.trim() || !draft.message.trim() || !draft.whatsappId) {
      toast.error("Completá nombre, mensaje y línea de WhatsApp.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/campaigns", {
        ...draft,
        whatsappId: Number(draft.whatsappId),
        followUpHours: Number(draft.followUpHours)
      });
      toast.success("Campaña creada como borrador.");
      setModalOpen(false);
      setDraft(initialDraft);
      await loadData();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async (campaign, status) => {
    try {
      await api.put("/campaigns/" + campaign.id + "/status", { status });
      await loadData();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSendBatch = async () => {
    if (!sendingCampaign) return;
    try {
      const { data } = await api.post(
        "/campaigns/" + sendingCampaign.id + "/send-batch",
        { limit: 5, delaySeconds: 6 }
      );
      toast.success(
        "Lote procesado: " + data.sent + " enviados, " + data.failed + " con error."
      );
      await loadData();
    } catch (err) {
      toastError(err);
    } finally {
      setSendingCampaign(null);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          sendingCampaign
            ? "Enviar hasta 5 mensajes de " + sendingCampaign.name + "?"
            : ""
        }
        open={Boolean(sendingCampaign)}
        onClose={() => setSendingCampaign(null)}
        onConfirm={handleSendBatch}
      >
        El lote usa una pausa de 6 segundos entre mensajes. Solo se envía a
        destinatarios habilitados y nunca a grupos.
      </ConfirmationModal>
      <Dialog
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nueva campaña</DialogTitle>
        <DialogContent dividers>
          <div className={classes.modalGrid}>
            <TextField
              label="Nombre interno"
              name="name"
              value={draft.name}
              onChange={handleDraftChange}
              variant="outlined"
              fullWidth
            />
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="campaign-whatsapp-label">Línea de WhatsApp</InputLabel>
              <Select
                labelId="campaign-whatsapp-label"
                label="Línea de WhatsApp"
                name="whatsappId"
                value={draft.whatsappId}
                onChange={handleDraftChange}
              >
                {connectedWhatsapps.map(whatsapp => (
                  <MenuItem key={whatsapp.id} value={whatsapp.id}>
                    {whatsapp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="campaign-segment-label">Segmento</InputLabel>
              <Select
                labelId="campaign-segment-label"
                label="Segmento"
                name="segment"
                value={draft.segment}
                onChange={handleDraftChange}
              >
                <MenuItem value="" disabled>
                  Seleccioná un segmento
                </MenuItem>
                {segments.map(segment => (
                  <MenuItem key={segment} value={segment}>
                    {segment}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Seguimiento a las (horas)"
              name="followUpHours"
              type="number"
              value={draft.followUpHours}
              onChange={handleDraftChange}
              inputProps={{ min: 1, max: 720 }}
              variant="outlined"
              fullWidth
            />
          </div>
          <TextField
            label="Mensaje"
            name="message"
            value={draft.message}
            onChange={handleDraftChange}
            variant="outlined"
            multiline
            rows={6}
            fullWidth
            margin="normal"
            helperText="Podés usar variables de contacto ya admitidas por Whaticket."
          />
          <Typography variant="body2" color="textSecondary">
            La campaña se crea como borrador. Requiere un segmento explícito y
            excluye siempre a los contactos marcados como “No contactar” y a
            los grupos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setModalOpen(false)}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={saving || connectedWhatsapps.length === 0}
            onClick={handleCreate}
          >
            Guardar borrador
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <MainHeaderButtonsWrapper>
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={connectedWhatsapps.length === 0}
            onClick={() => setModalOpen(true)}
          >
            Nueva campaña
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {followUps.length > 0 && (
        <Paper variant="outlined" className={classes.followUps}>
          <Typography variant="subtitle1">Seguimientos pendientes</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contacto</TableCell>
                <TableCell>Campaña</TableCell>
                <TableCell>Vence desde</TableCell>
                <TableCell align="right">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {followUps.map(followUp => (
                <TableRow key={followUp.id}>
				  <TableCell>{getContactDisplayName(followUp.contact)}</TableCell>
                  <TableCell>{followUp.campaign?.name}</TableCell>
                  <TableCell>
                    {followUp.followUpAt
                      ? new Date(followUp.followUpAt).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell align="right">
                    {followUp.ticketId && (
                      <Tooltip title="Abrir conversación">
                        <IconButton
                          size="small"
                          onClick={() =>
                            history.push("/tickets/" + followUp.ticketId)
                          }
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Campaña</TableCell>
              <TableCell>Línea</TableCell>
              <TableCell>Segmento</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Destinatarios</TableCell>
              <TableCell align="center">Enviados</TableCell>
              <TableCell align="center">Respuestas</TableCell>
              <TableCell>Mensaje</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map(campaign => (
              <TableRow key={campaign.id}>
                <TableCell>{campaign.name}</TableCell>
                <TableCell>{campaign.whatsapp?.name || "-"}</TableCell>
                <TableCell>{campaign.segment || "Todos"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={statusLabel[campaign.status] || campaign.status}
                    color={statusColor(campaign.status)}
                  />
                </TableCell>
                <TableCell align="center">{campaign.totalRecipients}</TableCell>
                <TableCell align="center">{campaign.sentCount}</TableCell>
                <TableCell align="center">{campaign.repliedCount}</TableCell>
                <TableCell className={classes.messageCell}>{campaign.message}</TableCell>
                <TableCell align="right" className={classes.actionCell}>
                  {campaign.status !== "completed" && (
                    <Tooltip title="Enviar lote de hasta 5">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={campaign.status === "paused" || campaign.whatsapp?.status !== "CONNECTED"}
                          onClick={() => setSendingCampaign(campaign)}
                        >
                          <SendIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {campaign.status === "paused" ? (
                    <Tooltip title="Reanudar">
                      <IconButton
                        size="small"
                        onClick={() => handleStatus(campaign, "active")}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    campaign.status !== "completed" && (
                      <Tooltip title="Pausar">
                        <IconButton
                          size="small"
                          onClick={() => handleStatus(campaign, "paused")}
                        >
                          <PauseIcon />
                        </IconButton>
                      </Tooltip>
                    )
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && campaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Todavía no hay campañas creadas.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Campaigns;
